/**
 * HedgeFundModePage.tsx — MER Algo Footprints v7.1H
 *
 * Single-pane HF workspace. hfView controls what fills the content area:
 *
 *   hfView = 'home'      → DecisionBand + module nav + two-column summary
 *   hfView = <module>    → DecisionBand + back button + full module panel only
 *
 * Only ONE content region is visible at a time. No stacked panes.
 */

import React, { useState, useEffect, useCallback } from 'react';

import { useStore }                               from '../../store';
import { getSymbolContext }                        from '../../selectors/symbolContext';
import { getThesis, subscribeThesis }              from '../../ai/thesisEngine';
import { useGammaSnapshot }                        from '../gamma/useGammaSnapshot';
import { LIVE_GAMMA_THESIS_ENABLED }               from '../gamma/gammaUiConfig';
import { buildHedgeFundViewModel }                 from './mappers/buildHedgeFundViewModel';
import { emptyViewModel, HedgeFundModeViewModel }  from './types/hedgeFundViewModel';
import { ContextValidationGrid }                   from './components/ContextValidationGrid';
import { ExecutionWorkspace }                      from './components/ExecutionWorkspace';
import { ThesisSummaryPanel }                      from './components/ThesisSummaryPanel';
import { JournalReviewDrawer }                     from './components/JournalReviewDrawer';
import { buildResearchSnapshot, captureAndPersistDecisionSnapshot } from '../../research/researchSnapshot';
import { evaluateSetupRisk }                       from '../../domains/risk/models/riskEngine';

import { ThesisPanel }    from '../thesis/ThesisPanel';
import { SMCPanel }       from '../smc/SMCPanel';
import { LiquidityPanel } from '../liquidity/LiquidityPanel';
import { GammaPanel }     from '../gamma/GammaPanel';
import { TrapPanel }      from '../traps/TrapPanel';
import { RegimePanel }    from '../regime/RegimePanel';
import { SetupsPanel }    from '../setups/SetupsPanel';
import { SMCOverlaySettings } from '../../types';

// ─── View type ────────────────────────────────────────────────────────────────

type HFView = 'home' | 'thesis' | 'smc' | 'liquidity' | 'gamma' | 'traps' | 'regime' | 'setups';

const HF_MODULES: { id: HFView; label: string; color: string }[] = [
  { id: 'thesis',    label: 'AI Thesis', color: 'var(--purple)' },
  { id: 'smc',       label: 'SMC',       color: 'var(--blue)'   },
  { id: 'liquidity', label: 'Liquidity', color: 'var(--purple)' },
  { id: 'gamma',     label: 'Gamma',     color: 'var(--purple)' },
  { id: 'traps',     label: 'Traps',     color: 'var(--amber)'  },
  { id: 'regime',    label: 'Regime',    color: 'var(--blue)'   },
  { id: 'setups',    label: 'Setups',    color: 'var(--gold)'   },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  symbol:              string;
  hfModule:            string;           // 'home' | module id — owned by App
  setHfModule:         (v: string) => void;
  smcOverlays:         SMCOverlaySettings;
  onSmcOverlaysChange: (o: SMCOverlaySettings) => void;
}

// ─── Shared: Decision band (always visible) ───────────────────────────────────

function DecisionBand({ vm }: { vm: HedgeFundModeViewModel }) {
  // Phase 2 (v9.2.0): redundancy trim. Symbol, BIAS, and CONF removed —
  // the persistent Global Trading Header now surfaces those fields.
  // What remains is the unique signal: setup state, setup name, readiness,
  // time sensitivity, and decision timestamp.
  const STATE_COLOR: Record<string, string> = { ready: '#4ade80', watch: '#facc15', avoid: '#64748b' };
  const stateColor = STATE_COLOR[vm.decision.state];

  return (
    <div className="hf-decision-band" style={{ borderBottomColor: `${stateColor}40` }}>
      {/* State badge — leads, since it's the most distinctive signal */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{
          fontSize: 12, fontWeight: 700, color: stateColor, fontFamily: 'var(--mono)',
          background: `${stateColor}15`, border: `1px solid ${stateColor}40`,
          borderRadius: 4, padding: '2px 10px', letterSpacing: 1.5, flexShrink: 0,
        }}>
          {vm.decision.state.toUpperCase()}
        </span>
      </div>

      {/* Setup */}
      {vm.decision.setupName && (
        <>
          <div style={{ width: 1, height: 40, background: '#1a3050', flexShrink: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            <span style={{ fontSize: 8, color: '#475569', fontFamily: 'var(--mono)', letterSpacing: 1 }}>SETUP</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {vm.decision.setupName}
            </span>
          </div>
        </>
      )}

      {/* Readiness — unique to this band (HUD shows confidence only) */}
      {vm.decision.readinessScore !== null && (
        <>
          <div style={{ width: 1, height: 40, background: '#1a3050', flexShrink: 0 }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 8, color: '#475569', fontFamily: 'var(--mono)', letterSpacing: 1 }}>READY</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 56, height: 3, background: '#1a3050', borderRadius: 2 }}>
                <div style={{ width: `${vm.decision.readinessScore}%`, height: '100%', background: '#a78bfa', borderRadius: 2 }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', fontFamily: 'var(--mono)', minWidth: 24 }}>{vm.decision.readinessScore}</span>
            </div>
          </div>
        </>
      )}

      {/* Time sensitivity + timestamp */}
      <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
        {vm.decision.timeSensitivity === 'immediate' && (
          <span style={{ fontSize: 10, fontWeight: 800, color: '#facc15', fontFamily: 'var(--mono)', letterSpacing: 1 }}>⚡ IMMEDIATE</span>
        )}
        {vm.decision.timeSensitivity === 'developing' && (
          <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'var(--mono)' }}>⏳ DEVELOPING</span>
        )}
        <span style={{ fontSize: 9, color: '#334155', fontFamily: 'var(--mono)' }}>
          {new Date(vm.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      </div>
    </div>
  );
}

// ─── Module nav strip ─────────────────────────────────────────────────────────

function HFModuleNav({ active, onSelect }: { active: string; onSelect: (id: string) => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch',
      borderBottom: '1px solid var(--border)',
      background: 'var(--navy)', flexShrink: 0, overflowX: 'auto',
    }}>
      {HF_MODULES.map(m => {
        const isActive = active === m.id;
        return (
          <button
            key={m.id}
            onClick={() => onSelect(m.id)}
            style={{
              padding: '8px 16px',
              background: isActive ? `${m.color}10` : 'none',
              border: 'none',
              borderBottom: isActive ? `2px solid ${m.color}` : '2px solid transparent',
              color: isActive ? m.color : 'var(--blue-dim)',
              fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 700, letterSpacing: 1,
              cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              transition: 'color 0.15s, border-color 0.15s, background 0.15s',
              marginBottom: -1,
            }}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Back bar (shown in module detail views) ──────────────────────────────────

function BackBar({ label, onBack }: { label: string; onBack: () => void }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '6px 16px',
      background: 'var(--bg1)',
      borderBottom: '1px solid var(--border)',
      flexShrink: 0,
    }}>
      <button
        onClick={onBack}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none',
          color: 'var(--blue-muted)', fontFamily: 'var(--mono)',
          fontSize: 11, fontWeight: 600, cursor: 'pointer',
          padding: '3px 0',
          transition: 'color 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--gold)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--blue-muted)')}
      >
        ← Back to HF
      </button>
      <div style={{ width: 1, height: 14, background: 'var(--border)' }} />
      <span style={{ fontSize: 10, color: 'var(--blue-dim)', fontFamily: 'var(--mono)', letterSpacing: 1 }}>
        {label.toUpperCase()}
      </span>
    </div>
  );
}

// ─── HF Home view ─────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: string }) {
  return (
    <div style={{
      fontSize: 8, fontWeight: 700, color: '#334155', fontFamily: 'var(--mono)',
      letterSpacing: 1.5, textTransform: 'uppercase',
      paddingBottom: 6, borderBottom: '1px solid #0d1e35', marginBottom: 2, flexShrink: 0,
    }}>
      {children}
    </div>
  );
}

function HFHome({
  vm, symbol, smcOverlays, onSmcOverlaysChange,
  onSaveSnapshot, onPass,
}: {
  vm: HedgeFundModeViewModel;
  symbol: string;
  smcOverlays: SMCOverlaySettings;
  onSmcOverlaysChange: (o: SMCOverlaySettings) => void;
  onSaveSnapshot: () => Promise<void>;
  onPass: () => void;
}) {
  const [accept]  = useState(() => () => {});
  const [discard] = useState(() => () => {});

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', flex: 1,
      minHeight: 0, overflowY: 'auto',
      padding: '12px 16px 18px', gap: 16,
    }}>
      {/* 1. Context Overview */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionLabel>Context Overview</SectionLabel>
        <ContextValidationGrid vm={vm} />
      </div>

      {/* 2. Execution (compact price + plan) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionLabel>Execution</SectionLabel>
        <ExecutionWorkspace
          vm={vm}
          symbol={symbol}
          smcOverlays={smcOverlays}
          onSaveSnap={onSaveSnapshot}
          onPass={onPass}
        />
      </div>

      {/* 3. Thesis Summary */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionLabel>Thesis Summary</SectionLabel>
        <ThesisSummaryPanel vm={vm} />
      </div>

      {/* 4. Review / Journal */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionLabel>Review / Journal</SectionLabel>
        <JournalReviewDrawer
          vm={vm}
          onAccept={accept}
          onDiscard={discard}
          forceOpen={vm.review.journalDraftAvailable}
        />
      </div>
    </div>
  );
}

// ─── Module detail view ───────────────────────────────────────────────────────

function HFModuleView({
  view, symbol, smcOverlays, onSmcOverlaysChange,
}: {
  view: HFView;
  symbol: string;
  smcOverlays: SMCOverlaySettings;
  onSmcOverlaysChange: (o: SMCOverlaySettings) => void;
}) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 4px' }}>
        {view === 'thesis'    && <ThesisPanel symbol={symbol} />}
        {view === 'smc'       && <SMCPanel symbol={symbol} overlays={smcOverlays} onOverlaysChange={onSmcOverlaysChange} />}
        {view === 'liquidity' && <LiquidityPanel symbol={symbol} />}
        {view === 'gamma'     && <GammaPanel symbol={symbol} />}
        {view === 'traps'     && <TrapPanel symbol={symbol} />}
        {view === 'regime'    && <RegimePanel symbol={symbol} />}
        {view === 'setups'    && <SetupsPanel symbol={symbol} />}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function HedgeFundModePage({
  symbol, hfModule, setHfModule, smcOverlays, onSmcOverlaysChange,
}: Props) {
  const { symbolData, settings, weights } = useStore();
  const data = symbolData[symbol];
  const ctx  = data?.candles?.length ? getSymbolContext(symbol, data, settings, weights) : null;

  const [thesis, setThesis] = useState(() => getThesis(symbol));
  useEffect(() => {
    setThesis(getThesis(symbol));
    return subscribeThesis(() => setThesis(getThesis(symbol)));
  }, [symbol]);

  const { snapshot: gammaSnap } = useGammaSnapshot(
    LIVE_GAMMA_THESIS_ENABLED ? symbol : '',
    { autoRefreshMs: LIVE_GAMMA_THESIS_ENABLED ? 30_000 : 0 }
  );

  const vm = ctx
    ? buildHedgeFundViewModel(symbol, ctx, thesis, gammaSnap ?? null)
    : emptyViewModel(symbol);

  const handleSaveSnapshot = useCallback(async () => {
    if (!ctx) return;
    const cur   = thesis?.current ?? null;
    const setup = ctx.bestActionableSetup ?? ctx.topDisplaySetup ?? null;
    const risk  = setup && ctx ? evaluateSetupRisk(setup, ctx, cur?.conviction ?? 0) : null;
    const snap  = buildResearchSnapshot(ctx, cur, setup, risk);
    await captureAndPersistDecisionSnapshot(snap, cur?.inputHash ?? null);
  }, [ctx, thesis]);

  const handlePass = useCallback(() => {}, []);

  // Derive view — coerce any legacy value to valid HFView
  const view = (hfModule as HFView) ?? 'home';
  const isHome = view === 'home';
  const activeModule = HF_MODULES.find(m => m.id === view);

  if (!data?.candles?.length) {
    const isLoading = !!data?.loading;
    const errMsg    = data?.error ?? null;
    return (
      <div className="hf-page" style={{ alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <div style={{ color: '#475569', fontFamily: 'var(--mono)', fontSize: 12, textAlign: 'center' }}>
          {isLoading
            ? <>Loading data for {symbol}…</>
            : errMsg
              ? <>Data error for {symbol}: {errMsg}</>
              : <>No data for {symbol}.<br />Fetch data or load a sample to use the Workspace.</>}
        </div>
      </div>
    );
  }

  return (
    <div className="hf-page">

      {/* Decision band — always visible */}
      <DecisionBand vm={vm} />

      {isHome ? (
        // ── HOME: module nav strip + two-column summary ──────────────────────
        <>
          <HFModuleNav active="home" onSelect={setHfModule} />
          <HFHome
            vm={vm}
            symbol={symbol}
            smcOverlays={smcOverlays}
            onSmcOverlaysChange={onSmcOverlaysChange}
            onSaveSnapshot={handleSaveSnapshot}
            onPass={handlePass}
          />
        </>
      ) : (
        // ── MODULE VIEW: back bar + full panel, no home content ──────────────
        <>
          <BackBar
            label={activeModule?.label ?? view}
            onBack={() => setHfModule('home')}
          />
          <HFModuleNav active={view} onSelect={setHfModule} />
          <HFModuleView
            view={view}
            symbol={symbol}
            smcOverlays={smcOverlays}
            onSmcOverlaysChange={onSmcOverlaysChange}
          />
        </>
      )}

    </div>
  );
}
